import { convertToProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import type { RemoteDataGraphQlResponse } from './RemoteDataGraphQlResponse.types.tsx';
import { canCallORS, fetchORSDataSSR, type PrefetchedSmartlinkData } from './fetchORSDataSSR.tsx';

const SMART_LINKS_FIRST = 4;
const FETCH_CONFLUENCE_SMARTLINK_DATA = 'fetch-confluence-smartlink-data';

type CloudId = string;

type ConfluencePage = {
	href?: string | null;
	id?: string | null;
	globalId?: string | null;
	title?: string | null;
	type?: string | null;
};

type FailedRemoteLink = {
	id?: string | null;
	error?: string | null;
	link?: {
		href?: string | null;
		id?: string | null;
		globalId?: string | null;
	};
};

function extractSmartLinkUrls(
	remoteIssueGraphqlData: RemoteDataGraphQlResponse | null | undefined,
): string[] {
	const linkedPages = remoteIssueGraphqlData?.remoteLinks?.confluencePages.linkedPages.pages;
	const mentionedPages = remoteIssueGraphqlData?.remoteLinks?.confluencePages.mentionedPages.pages;

	const smartLinkUrls: string[] = [];

	const addUrlFromPage = (page: ConfluencePage | FailedRemoteLink) => {
		// For failed remote links, check if there's a link href
		if ('link' in page && page.link?.href) {
			smartLinkUrls.push(page.link.href);
		} else if ('href' in page && page.href) {
			// For successful confluence pages, use the href
			smartLinkUrls.push(page.href);
		}
	};

	if (linkedPages) {
		linkedPages.forEach(addUrlFromPage);
	}

	if (mentionedPages) {
		mentionedPages.forEach(addUrlFromPage);
	}

	// Remove duplicates and cap at SMART_LINKS_FIRST
	return [...new Set(smartLinkUrls)].slice(0, SMART_LINKS_FIRST);
}

export const fetchConfluenceSmartlinkData = async (
	remoteIssueGraphqlData: RemoteDataGraphQlResponse | null | undefined,
	cloudId: CloudId,
): Promise<PrefetchedSmartlinkData[] | null> => {
	if (!canCallORS()) {
		return null;
	}

	const urls = extractSmartLinkUrls(remoteIssueGraphqlData);
	const projectTypeData = remoteIssueGraphqlData?.projectType;
	const projectType = projectTypeData ? convertToProjectType(projectTypeData) : undefined;

	if (urls.length === 0) {
		return null;
	}

	return fetchORSDataSSR(urls, FETCH_CONFLUENCE_SMARTLINK_DATA, projectType, cloudId);
};
